<template>
  <div>
    <clinician-app-layout>
      <div class="mx-auto sm:px-6 lg:px-8 py-6">
        <div class="pb-5 sm:flex sm:items-center sm:justify-between">
          <h2 class="text-3xl text-vice-teal-900 font-semibold leading-tight">
            Device & session setup
          </h2>
        </div>
        <div>
          <div class="select-usecase relative">
            <div class="flex justify-end items-center">
              <jet-label for="use-case" value="Use-case:" class="pl-4 pr-2 bg-light-grey z-10" />
              <select
                v-model="currentUseCaseId"
                id="use-case"
                class="
                  border-gray-300
                  focus:ring-vice-teal-500
                  focus:border-vice-teal-500
                  rounded-md
                  shadow-sm
                  sm:text-sm
                  text-gray-900
                  z-10
                "
              >
                <option v-for="useCase in useCasesNames" :value="useCase.id" :key="useCase.id">
                  {{ useCase.name }}
                </option>
              </select>
            </div>
          </div>
          <ul v-if="currentUseCase && currentUseCase.device_type_usage_priorities">
            <li
              v-for="(
                currentUseCasePrio, currentUseCasePrioIndex
              ) in currentUseCase.device_type_usage_priorities"
              :key="currentUseCasePrio.id"
              class="mb-8 relative"
            >
              <div class="use-case-priority">
                <div class="use-case-priority-header">
                  <div class="use-case-name-priority">
                    <div class="relative flex items-center h-10">
                      <div class="absolute inset-0 flex items-center" aria-hidden="true">
                        <div class="w-full border-t border-gray-300" />
                      </div>
                      <div class="relative flex justify-start pb-px">
                        <span
                          class="pr-3 pb-px bg-light-grey text-lg font-medium text-vice-teal-700"
                        >
                          {{ currentUseCasePrio.name }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="use-case-priority-description">
                    <div class="relative flex w-2/3">
                      <span class="pr-3 bg-light-grey text-xs font-light">
                        {{ currentUseCasePrio.description }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="use-case-slots mt-6 mb-6">
                  <ul v-if="currentUseCasePrio && currentUseCasePrio.device_type_use_case_slots">
                    <div class="grid grid-cols-3 w-full gap-6">
                      <li v-if="currentUseCasePrioIndex === 0">
                        <TestConnectionSlot />
                      </li>
                      <li v-if="currentUseCasePrioIndex === 0">
                        <DefaultDevicesSlot></DefaultDevicesSlot>
                      </li>
                      <li
                        v-for="slot in currentUseCasePrio.device_type_use_case_slots"
                        :key="slot.id"
                      >
                        <UseCaseSlot
                          :useCaseId="currentUseCase.id"
                          :deviceSlotId="slot.id"
                          :deviceTypeLabel="slot.device_type.label"
                          :deviceKind="slot.device_kind"
                          :deviceTypeIcon="slot.device_type.icon"
                          :uiNotificationType="slot.ui_notification_type"
                          :uiNotificationTitle="slot.ui_notification_title"
                          :uiNotification="slot.ui_notification"
                          :storedClinicianDevice="slot.stored_clinician_device?.device_data"
                        ></UseCaseSlot>
                      </li>
                    </div>
                  </ul>
                </div>
              </div>
            </li>
          </ul>
        </div>
        <SetDeviceModal></SetDeviceModal>
        <DeleteDeviceModal></DeleteDeviceModal>
      </div>
    </clinician-app-layout>
  </div>
</template>

<script>
import { usePage } from '@inertiajs/inertia-vue3';
import { ref, computed, watch } from 'vue';
import useDeviceAndSessionSetupStore from '@/Stores/useDeviceAndSessionSetupStore';
import SetDeviceModal from '@/Components/Modals/DeviceAndSessionSetup/SetDeviceModal.vue';
import DeleteDeviceModal from '@/Components/Modals/DeviceAndSessionSetup/DeleteDeviceModal.vue';
import ClinicianAppLayout from '@/Layouts/ClinicianAppLayout.vue';
import UseCaseSlot from '@/Components/DeviceAndSessionSetup/UseCaseSlot.vue';
import JetLabel from '@/Jetstream/Label.vue';
import DefaultDevicesSlot from '@/Components/DeviceAndSessionSetup/DefaultDevicesSlot.vue';
import TestConnectionSlot from '@/Components/DeviceAndSessionSetup/TestConnectionSlot.vue';

export default {
  name: 'DeviceAndSessionSetup',
  components: {
    TestConnectionSlot,
    ClinicianAppLayout,
    UseCaseSlot,
    SetDeviceModal,
    DeleteDeviceModal,
    JetLabel,
    DefaultDevicesSlot,
  },
  setup() {
    const { props } = usePage();

    const deviceAndSessionSetupStore = useDeviceAndSessionSetupStore();

    const deviceTypesUseCases = ref([]);
    const currentUseCase = ref({});
    const currentUseCaseId = ref(null);

    // storing use cases to Pinia storage
    deviceAndSessionSetupStore.setDeviceTypesUseCases(props.value.deviceTypesUseCases);

    // storing clinician default devices (microphone and speaker) to Pinia store
    deviceAndSessionSetupStore.setDefaultClinicianDevices(props.value.defaultDevices);

    // getting use cases from Pinia storage
    deviceTypesUseCases.value = deviceAndSessionSetupStore.getDeviceTypesUseCases;

    currentUseCaseId.value = deviceTypesUseCases.value.length
      ? deviceTypesUseCases.value[0].id
      : null;

    currentUseCase.value = deviceTypesUseCases.value.find(
      (useCase) => useCase.id === currentUseCaseId.value,
    );

    const useCasesNames = computed(() => {
      const names = [];
      Object.values(deviceTypesUseCases.value).forEach((useCase) => {
        names.push({
          id: useCase.id,
          name: useCase.name,
        });
      });
      return names;
    });

    watch(currentUseCaseId, (newValue) => {
      currentUseCase.value = deviceTypesUseCases.value.find((useCase) => useCase.id === newValue);
    });

    return {
      useCasesNames,
      currentUseCaseId,
      currentUseCase,
    };
  },
};
</script>

<style scoped>
.select-usecase {
  top: 40px;
}
</style>
